import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faTrash, faUpload } from '@/fontawesome';
import { FILE_SIZE_LIMITS, formatFileSize } from '@/config';

interface ThumbnailUploadProps {
  onThumbnailChange: (fileData: { file: File; base64: string } | null) => void;
  disabled?: boolean;
  existingThumbnail?: string;
  className?: string;
  required?: boolean;
  initialThumbnail?: { file: File; base64: string } | null;
}

export default function ThumbnailUpload({ 
  onThumbnailChange, 
  disabled = false, 
  existingThumbnail,
  className = '',
  required = false,
  initialThumbnail
}: ThumbnailUploadProps) {
  const [preview, setPreview] = useState<string | null>(initialThumbnail?.base64 || existingThumbnail || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update preview when initialThumbnail changes
  useEffect(() => {
    if (initialThumbnail?.base64) {
      setPreview(initialThumbnail.base64);
    }
  }, [initialThumbnail]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Bitte wählen Sie eine Bilddatei aus (PNG, JPG, etc.)');
      return;
    }

    // Validate file size
    if (file.size > FILE_SIZE_LIMITS.THUMBNAIL_MAX) {
      alert(`Die Datei ist zu groß. Maximal ${formatFileSize(FILE_SIZE_LIMITS.THUMBNAIL_MAX)} sind erlaubt.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Data = e.target?.result as string;
      setPreview(base64Data);
      onThumbnailChange({ file, base64: base64Data });
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setPreview(null);
    onThumbnailChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Thumbnail {required ? '*' : '(optional)'}
      </label>
      
      <div className="flex items-center space-x-4">
        {/* Preview or Upload Area */}
        <div 
          onClick={handleClick}
          className={`
            relative w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 
            rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 dark:hover:border-blue-400
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {preview ? (
            <img 
              src={preview} 
              alt="Thumbnail preview" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <FontAwesomeIcon icon={faImage} className="w-8 h-8" />
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2">
          <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30"
          >
            <FontAwesomeIcon icon={faUpload} className="w-3 h-3 mr-1" />
            {preview ? 'Ändern' : 'Hochladen'}
          </button>
          
          {preview && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 cursor-pointer dark:bg-red-900/20 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              <FontAwesomeIcon icon={faTrash} className="w-3 h-3 mr-1" />
              Entfernen
            </button>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />

      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        PNG, JPG oder andere Bildformate. Maximal {formatFileSize(FILE_SIZE_LIMITS.THUMBNAIL_MAX)}.
      </p>
    </div>
  );
}